/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.FitBaseType

/**
 * Writes FIT files.
 *
 * ```kotlin
 * val bytes = encodeFit {
 *     write(FileIdMesg().apply { type = File.ACTIVITY; timeCreated = Clock.System.now() })
 *     write(RecordMesg().apply { heartRate = 140u })
 * }
 * ```
 *
 * A FIT file must open with a `file_id` message; nothing here enforces that,
 * but readers will reject a file without one.
 *
 * [close] is not optional. The file header states the size of the data that
 * follows it, which is only known once the last message is written, and the
 * file ends with a CRC over everything before it. Both are produced there.
 */
public class FitEncoder(
    /**
     * Protocol version byte to stamp into the header.
     *
     * Raw rather than a [ProtocolVersion] so that re-encoding a file written by
     * a producer whose version the enum does not name reproduces its header.
     */
    private val protocolVersionByte: UByte = ProtocolVersion.V2_0.value,
    private val profileVersion: UShort = Fit.PROFILE_VERSION,
    /**
     * Architecture announced by the message definitions this encoder emits, and
     * used for every multi-byte field value written under them. The file header
     * itself is always little-endian, as the protocol requires.
     */
    public val endianness: Endianness = Endianness.LITTLE,
) {
    public constructor(
        protocolVersion: ProtocolVersion,
        profileVersion: UShort = Fit.PROFILE_VERSION,
        endianness: Endianness = Endianness.LITTLE,
    ) : this(protocolVersion.value, profileVersion, endianness)

    private val writer = ByteWriter()

    /** Which layout each of the 16 local message numbers currently holds. */
    private val localDefinitions = arrayOfNulls<MesgDefinition>(Fit.NUM_LOCAL_MESG_DEFS)
    private var nextLocalMesgNum = 0
    private var closed = false

    /** (index, application) pairs whose `developer_data_id` is already in the file. */
    private val announcedDeveloperIds = HashSet<Pair<UByte, List<Byte>?>>()

    init {
        // Reserve the header; its data size is only known at close().
        writer.writeBytes(ByteArray(Fit.HEADER_WITH_CRC_SIZE))
    }

    /** Number of messages written so far. */
    public var mesgCount: Int = 0
        private set

    public fun write(mesgs: Iterable<Mesg>): FitEncoder {
        for (mesg in mesgs) write(mesg)
        return this
    }

    /**
     * Writes [mesg], emitting a message definition first if the layout it needs
     * is not already the one bound to a local message number.
     */
    public fun write(mesg: Mesg): FitEncoder {
        check(!closed) { "this encoder is closed" }

        val definition = MesgDefinition.of(mesg, 0u, endianness)
        val local = localMesgNumFor(definition)

        val bound = MesgDefinition(
            localMesgNum = local,
            globalMesgNum = definition.globalMesgNum,
            endianness = definition.endianness,
            fieldDefinitions = definition.fieldDefinitions,
            developerFieldDefinitions = definition.developerFieldDefinitions,
        )

        if (localDefinitions[local.toInt()]?.hasSameLayout(bound) != true) {
            bound.write(writer)
            localDefinitions[local.toInt()] = bound
        }

        mesg.write(writer, bound)
        mesgCount++
        return this
    }

    /**
     * Declares a developer field, writing the `developer_data_id` message that
     * claims its index (once per application/index pair) followed by its
     * `field_description` message.
     *
     * A data record can only carry a [DeveloperField] whose declaration precedes
     * it in the file, so call this before writing the first message holding a
     * field made from [description] — typically one obtained from
     * [FitMessages.developerFieldDescriptions] after decoding, which closes the
     * decode → re-encode loop, or built by hand alongside
     * [DeveloperFieldDescription.createField].
     *
     * `field_description` stores scale and offset as single bytes; a
     * [description] whose values do not fit those types (never the case for one
     * produced by the decoder) is declared with them truncated.
     */
    public fun registerDeveloperField(description: DeveloperFieldDescription): FitEncoder {
        check(!closed) { "this encoder is closed" }

        val application = description.applicationId
        if (announcedDeveloperIds.add(description.developerDataIndex to application?.toList())) {
            write(
                DeveloperDataIdMesg().apply {
                    developerDataIndex = description.developerDataIndex
                    application?.let { id -> applicationId = id.map { it.toUByte() } }
                    description.applicationVersion?.let { applicationVersion = it }
                },
            )
        }

        write(
            FieldDescriptionMesg().apply {
                developerDataIndex = description.developerDataIndex
                fieldDefinitionNumber = description.fieldDefinitionNumber
                fitBaseTypeId = FitBaseType.fromValue(description.baseType.id)
                fieldName = listOf(description.fieldName)
                if (description.units.isNotEmpty()) units = listOf(description.units)
                if (description.scale != Fit.FIELD_DEFAULT_SCALE) scale = description.scale.toInt().toUByte()
                if (description.offset != Fit.FIELD_DEFAULT_OFFSET) offset = description.offset.toInt().toByte()
                description.nativeFieldNum?.let { nativeFieldNum = it }
            },
        )
        return this
    }

    /** Writes a definition explicitly, claiming its local message number. */
    public fun write(mesgDefinition: MesgDefinition): FitEncoder {
        check(!closed) { "this encoder is closed" }
        mesgDefinition.write(writer)
        localDefinitions[mesgDefinition.localMesgNum.toInt()] = mesgDefinition
        return this
    }

    /**
     * Finishes the file: patches the header's data size and header CRC, then
     * appends the file CRC. Returns the complete file.
     */
    public fun close(): ByteArray {
        check(!closed) { "this encoder is already closed" }
        closed = true

        val dataSize = writer.size - Fit.HEADER_WITH_CRC_SIZE
        val header = FileHeader(
            headerSize = Fit.HEADER_WITH_CRC_SIZE,
            protocolVersionByte = protocolVersionByte,
            profileVersion = profileVersion,
            dataSize = dataSize.toUInt(),
        )
        header.updateCrc()
        writer.replaceRange(0, header.toByteArray())

        writer.writeUShort(writer.crc())
        return writer.toByteArray()
    }

    /**
     * Finds the local message number to write [definition] under.
     *
     * Reuses the slot already holding this exact layout, so a run of records
     * costs one definition record rather than one per message. Otherwise takes
     * the next slot round-robin: there are only 16, and evicting the
     * least-recently-claimed is what the other SDKs do.
     */
    private fun localMesgNumFor(definition: MesgDefinition): UByte {
        for (i in localDefinitions.indices) {
            // hasSameLayout ignores the local number, so the incoming definition
            // can be compared directly against each slot, whatever it is bound to.
            if (localDefinitions[i]?.hasSameLayout(definition) == true) return i.toUByte()
        }

        for (i in localDefinitions.indices) {
            if (localDefinitions[i] == null) return i.toUByte()
        }

        val local = nextLocalMesgNum
        nextLocalMesgNum = (nextLocalMesgNum + 1) % Fit.NUM_LOCAL_MESG_DEFS
        localDefinitions[local] = null
        return local.toUByte()
    }
}

/**
 * Encodes a FIT file from [block], closing the encoder for you.
 *
 * ```kotlin
 * val bytes = encodeFit { write(FileIdMesg()) }
 * ```
 *
 * [endianness] is the architecture the emitted definitions announce; little-endian
 * is what every Garmin producer writes, and reading is unaffected either way.
 */
public inline fun encodeFit(
    endianness: Endianness = Endianness.LITTLE,
    block: FitEncoder.() -> Unit,
): ByteArray = FitEncoder(endianness = endianness).apply(block).close()
